import * as React from "react";
import * as ReactDOM from "react-dom";
import * as AC from "adaptivecards";
import * as FluentUI from "@fluentui/react";
import { adaptiveCardSearch } from "../adaptiveCardSearchBridge.js";
import { ThemeProvider } from "@fluentui/react";
import { Fluent2WebDarkTheme } from '@fluentui/fluent2-theme';

export const getDiv = () => document.createElement("div");


export class DataQuery extends AC.SerializableObject {
    static datasetProperty = new AC.StringProperty(AC.Versions.v1_4, "dataset");

    get dataset() {
        return this.getValue(DataQuery.datasetProperty);
    }

    getSchemaKey() {
        return "Data.Query";
    }

}

export class InputChoiceSetFluentUI extends AC.ChoiceSetInput {
    static isPeopleProperty = new AC.BoolProperty(AC.Versions.v1_4, "-ms-isPeople", false)
    static choicesDataProperty = new AC.SerializableObjectProperty(
        AC.Versions.v1_4,
        "choices.data",
        DataQuery,
        true
    );

    get choicesData() {
        return this.getValue(InputChoiceSetFluentUI.choicesDataProperty);
    }

    _value;

    get value() {
        return this._value;
    }

    isSet() {
        return this._value ? true : false;
    }

    _selectedValues = []

    internalRender() {
        if (this.defaultValue) {
            this._value = this.defaultValue;
            this._selectedValues = this.defaultValueToArray(this.defaultValue);
        }
        const element = getDiv();
        // Fixes bug where control does not focus when not valid because div is not focusable
        element.tabIndex = -1;
        ReactDOM.render(this.renderReact(), element);
        element.style.width = "100%";
        return element;
    }


    renderReact = () => {
        if (this.choicesData) {
            // const isPeople = this.getValue(InputChoiceSetFluentUI.isPeopleProperty);
            // return isPeople ? <this.PeoplePicker /> : <this.Picker />;
            return <this.Picker />;
        }

        return this.isCompact ?
            <this.ComboBox /> :
            this.createExpandedView()
    };

    PeoplePicker = () => {
        return <FluentUI.NormalPeoplePicker
            onResolveSuggestions={this.resolvePeoplePickerSuggestions}
        //getTextFromItem={(item) => item.name} 
        />
    };

    Picker = () => {
        let theme;
        let styleOverrides = {};
        if (this.hostConfig?.containerStyles?.default?.backgroundColor !== "#ffffff") {
            theme = Fluent2WebDarkTheme;
        }
        else {
            // Get rid of the default black border in light theme
            const backgroundColor = "#F3F2F1";
            styleOverrides = {
                text: {
                    backgroundColor: backgroundColor,
                    borderColor: backgroundColor,
                    selectors: {
                        ":hover": {
                            borderColor: backgroundColor, // prevent black hover border
                        },
                        ":focus-within": {
                            borderColor: backgroundColor,
                        },
                        "::after": {
                            borderColor: backgroundColor, // some Fluent states draw border via pseudo-element
                        }
                    }
                }
            }
        }

        const pickerSuggestionsProps = {
            noResultsFoundText: 'Search Salesforce',
        };

        return <ThemeProvider theme={theme} applyTo="body">
            <FluentUI.TagPicker
                onChange={this.handlePickerChange}
                defaultSelectedItems={this.getDefaultPickerItems()}
                styles={styleOverrides}
                inputProps={{ placeholder: this.placeholder }}
                itemLimit={this.isMultiSelect ? undefined : 1}
                required={this.isRequired}
                pickerSuggestionsProps={pickerSuggestionsProps}
                onEmptyResolveSuggestions={this.handleEmptyResolveSuggestions}
                onResolveSuggestions={this.resolvePickerSuggestions}
                resolveDelay={300}
                getTextFromItem={(item) => item.title} />
        </ThemeProvider>
    };

    handleEmptyResolveSuggestions = (selectedItems) => {
        return this.choices.map(c => ({ key: c.value, name: c.title }));
    }

    resolvePeoplePickerSuggestions = async (filter, selectedItems) => {
        if (!this.choicesData) return [];
        return adaptiveCardSearch(filter, this.choicesData.dataset, {});
    };

    resolvePickerSuggestions = async (filter, selectedItems) => {
        let items = this.choices;
        if (this.choicesData) {
            const json = await adaptiveCardSearch(filter, this.choicesData.dataset, {});
            if (typeof json === 'object') {
                // {"type":"application/vnd.microsoft.search.searchResponse","value":{"results":[{"title":"salesforce.com (415) 901-7000","value":"001Ek00001p2QDEIA2"}]}}
                items = json.value?.results;
            }
        }
        // tranlate into expected structure
        return items.map(item => ({ key: item.value, name: item.title }));
    };




    ComboBox = () => {
        return <FluentUI.Dropdown
            onChange={this.handleComboBoxChange}
            id={this.id}
            options={this.inputChoicesToComboBoxOptions(this.choices)}
            multiSelect={this.isMultiSelect}
            defaultSelectedKey={this.isMultiSelect ? undefined : this.defaultValue}
            defaultSelectedKeys={this.isMultiSelect ? this.defaultValueToArray(this.defaultValue) : undefined}
            defaultValue={this.defaultValue}
            multiSelectDelimiter=", "
        //selectedKey={this._value}
        //selectedKeys={this.isMultiSelect ? this._selectedValues : undefined}
        />
    }

    createExpandedView = () => {
        return this.isMultiSelect ?
            this.inputChoicesToCheckbox() :
            this.createChoiceGroup();
    }

    createChoiceGroup = () => {
        return <FluentUI.ChoiceGroup
            id={this.id}
            onChange={this.handleChoiceGroupChange}
            options={this.inputChoicesToChoiceGroupOptions(
                this.choices,
                this.defaultValueToArray(this.defaultValue))}
            multiple={this.isMultiSelect}
        />
    }

    inputChoicesToCheckbox = () => {
        return <React.Fragment>
            {this.choices.map((c, i) =>
                <FluentUI.Checkbox
                    key={c.value}
                    label={c.title}
                    defaultChecked={this.defaultValueToArray(this.defaultValue).includes(c.value)}
                    onChange={(ev, checked) => {
                        this.updateMultiselectData(checked, c.value);
                        //ev.stopPropagation();
                        //ev.preventDefault();
                    }}
                    styles={{
                        root: {
                            paddingTop: i > 0 ? this.hostConfig.spacing.default : 0,
                        },
                    }}
                />,
            )}
        </React.Fragment>
    }

    defaultValueToArray = (value) => {
        if (value) {
            return value.split(",");
        }
        return [];
    }

    inputChoicesToComboBoxOptions = (choices) => {
        return choices.map(c => ({ key: c.value, text: c.title }));
    }

    inputChoicesToChoiceGroupOptions = (choices, value) => {
        return choices.map(c => ({ key: c.value, text: c.title, checked: value.includes(c.value) }));
    }

    handleChoiceGroupChange = (ev, option) => {
        this._value = option.key;
        //ev.stopPropagation();
        //ev.preventDefault();
    }

    getDefaultPickerItems = () => {
        const defaultItems = this.defaultValueToArray(this.defaultValue);
        return this.choices.filter(c => defaultItems.includes(c.value)).map(c => ({ key: c.value, name: c.title }));
    }

    handlePickerChange = (selectedItems) => {
        this._value = selectedItems.map(item => item.key).join(",");
    }

    handleComboBoxChange = (event, item) => {
        if (this.isMultiSelect) {
            this.updateMultiselectData(item.selected, item.key);
        } else {
            this._value = `${item.key}`;
        }
        // event.stopPropagation();
        // event.preventDefault();
    }

    updateMultiselectData = (selected, key) => {
        selected ? this._selectedValues.push(key) : this.removeItemFromArray(this._selectedValues, key);
        this._value = this._selectedValues.join(",");
    }

    removeItemFromArray = (arr, item) => {
        const index = arr.indexOf(item);
        arr.splice(index, 1);
    }
}
